import { useEffect, useMemo, useState } from "react";
import * as Yup from "yup";
import classNames from "classnames";
import { BreadCrumb, Button, MediaUploader, PageTitle, Ratings, StepsIndicator } from "@/_component/Common";
import MainAdsLayout from "@/_component/MainAdsLayout";
import { Error, Success } from "@/_service/notification/notification";
import { useAuthContext, useLanguageContext } from "@/hooks";
import { blobUrlToFile, composeTemplateImage, constructValidationError, dataUrlToFile, renderError, timeout, validateSchema } from "@/utility";
import StoryPreviewCard from "./StoryPreviewCard";
import { createStory, uploadMediaFile, getStoryDetails, updateStory, deleteMediaFile } from "@/apis";
import { useNavigate, useSearchParams } from "react-router-dom";
import type { MediaItem as UploaderMediaItem } from "@/_component/Common";
import { MediaType as UploaderMediaType } from "@/types";


export type FieldConfig = {
  name: string;
  label: string;
  placeholder?: string;
  type?: "text" | "textarea";
  maxLength?: number;
  required?: boolean;
  colClass?: string;
};

type ThumbnailOption = {
  id?: string | undefined;
  urlKey: string; // translation key that resolves to absolute url
  localUrl: string;
};

type StoryWizardProps = {
  headingPart1: string;
  headingPart2: string;
  breadcrumbs: string[];
  thumbnails: ThumbnailOption[];
  fields: FieldConfig[];
  includeRating?: boolean;
  sideBanner?: boolean;
  variant: "user-story" | "agri-expert";
};

type StoryFormState = {
  [key: string]: any;
  templateImageUrl?: string;
  thumbnailImageUrl?: string;
  totalExperience?: number;
  rating?: number;
  id?: string;
};

type StoryMediaItem = UploaderMediaItem & {
  mediaType?: UploaderMediaType;
  isNew?: boolean;
  isDeleted?: boolean;
};

const buildSchema = (fields: FieldConfig[], includeRating: boolean) => {
  const shape: Record<string, any> = {};

  fields.forEach((field) => {
    let validator = Yup.string();
    if (field.required) {
      validator = validator.required(`${field.label} is required`);
    }
    if (field.maxLength) {
      validator = validator.max(field.maxLength, `${field.label} max ${field.maxLength} characters`);
    }
    shape[field.name] = validator;
  });

  if (includeRating) {
    shape.rating = Yup.number()
      .min(1, "Rating is required")
      .max(5, "Max 5 rating")
      .required("Rating is required");
  }

  return Yup.object().shape(shape);
};

export function StoryWizard({
  headingPart1,
  headingPart2,
  breadcrumbs,
  thumbnails,
  fields,
  includeRating = false,
  sideBanner = true,
  variant,
}: StoryWizardProps) {
  const { language } = useLanguageContext();
  const { user } = useAuthContext();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const storyId = searchParams.get("storyId");

  const [step, setStep] = useState<1 | 2 | 3>(1);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingStory, setIsLoadingStory] = useState(false);
  const [errors, setErrors] = useState<Record<string, string> | any | null>(null);
  const [mediaList, setMediaList] = useState<StoryMediaItem[]>([]);
  const [composedPreviewUrl, setComposedPreviewUrl] = useState<string>("");
  const [isComposing, setIsComposing] = useState(false);
  const [editStoryId, setEditStoryId] = useState<string | null>(null);
  const steps = useMemo(
    () => [
      { step: 1, displayCode: "label.details", name: "Details" },
      { step: 2, displayCode: "label.upload_image", name: "Upload" },
      { step: 3, displayCode: "label.story_preview", name: "Preview" },
    ],
    []
  );

  const [formData, setFormData] = useState<StoryFormState>(() => {
    const initial: StoryFormState = { rating: includeRating ? 0 : undefined };
    fields.forEach((field) => {
      initial[field.name] = "";
    });
    return initial;
  });

  const resolvedThumbnails = useMemo(
    () =>
      thumbnails.map((thumb, index) => ({
        ...thumb,
        resolvedUrl: language(thumb.urlKey) || thumb.urlKey,
        isSelected: index === 0,
      })),
    [thumbnails, language]
  );

  const [thumbnailState, setThumbnailState] = useState(resolvedThumbnails);

  const schema = useMemo(() => buildSchema(fields, includeRating), [fields, includeRating]);

  const selectedThumbnail = useMemo(
    () => thumbnailState.find((t) => t.isSelected) || thumbnailState[0],
    [thumbnailState]
  );

  const isLastStep = step === steps.length;

  const updateField = (name: string, value: any) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const selectThumbnailTemplate = (idx: number) => {
    setThumbnailState((prev) =>
      prev.map((thumb, index) => ({
        ...thumb,
        isSelected: idx === index,
      }))
    );
    const thumb = thumbnailState[idx];
    updateField("templateImageUrl", thumb?.localUrl);
  };

  const hydrateEditStory = (story: any) => {
    if (!story) return;
    setEditStoryId(story?.id || storyId || null);

    const normalizedMedia: StoryMediaItem[] = (story?.mediaAccessList || []).map((m: any) => {
      const mType = (m.mediaType as UploaderMediaType) || "IMAGE";
      return {
        id: m.id || m.mediaId,
        url: m.url,
        mediaType: mType,
        type: mType,
        isNew: false,
        isDeleted: false,
      };
    });
    const firstImage = normalizedMedia.find((m) => (m.mediaType || m.type) === "IMAGE");

    setFormData((prev) => ({
      ...prev,
      ...story,
      templateImageUrl: story?.templateImageUrl || prev.templateImageUrl,
      thumbnailImageUrl: firstImage?.url || prev.thumbnailImageUrl,
      rating: story?.rating ?? prev.rating,
      experience: story?.experience ?? prev.experience,
    }));

    setMediaList(normalizedMedia);
    if (story?.thumbnailImageUrl) {
      setComposedPreviewUrl(story.thumbnailImageUrl);
    } else if (firstImage?.url) {
      setComposedPreviewUrl(firstImage.url);
    }

    if (story?.templateImageUrl) {
      setThumbnailState((prev) =>
        prev.map((t) => ({
          ...t,
          isSelected:
            t.localUrl === story.templateImageUrl ||
            t.resolvedUrl === story.templateImageUrl ||
            t.urlKey === story.templateImageUrl,
        }))
      );
    }
  };

  const validateStepOne = (): boolean => {
    try {
      validateSchema(schema, formData);
      setErrors(null);
      return true;
    } catch (err: any) {
      const validationErrors = constructValidationError(err);
      setErrors(validationErrors);
      timeout(() => setErrors(null), 5000);
      return false;
    }
  };

  const loadStoryIfNeeded = async () => {
    if (!storyId) return;
    setIsLoadingStory(true);
    try {
      const res = await getStoryDetails(storyId);
      const dto = res?.data?.response?.userStoryDTO;
      const story = Array.isArray(dto) ? dto[0] : dto;
      hydrateEditStory(story);
    } catch (err) {
      console.error(err);
      Error(language("msg.failed_to_load_story") || "Failed to load story");
    } finally {
      setIsLoadingStory(false);
    }
  };

  useEffect(() => {
    loadStoryIfNeeded();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storyId]);

  const getTemplateComposeConfig = (templateId: string | undefined, templateSrc: string | undefined) => {
    const baseTextConfig = {
      fontFamily: "Poppins, sans-serif",
      textAlign: "left",
      fontSize: 16,
      lineHeight: 20,
      color: "#ffffff"
    };

    const configs = [
      {
        match: "t1",
        overlayConfig: { x: 340, y: 89, width: 212, height: 225, clipAsCircle: true },
        textConfigs: [
          { x: 36, y: 180, maxWidth: 320, ...baseTextConfig },
        ],
      },
      {
        match: "t2",
        overlayConfig: { x: 324, y: 52, width: 277, height: 288, clipAsCircle: false },
        textConfigs: [
          { x: 24, y: 180, maxWidth: 300, ...baseTextConfig },
        ],
      },
      {
        match: "t3",
        overlayConfig: { x: 0, y: 0, width: 704, height: 280, clipAsCircle: false },
        textConfigs: [
          { x: 24, y: 350, maxWidth: 510, ...baseTextConfig },
        ],
      },
      {
        match: "t4",
        overlayConfig: { x: 290, y: 0, width: 240, height: 400, clipAsCircle: false },
        textConfigs: [
          { x: 24, y: 200, maxWidth: 270, ...baseTextConfig },
        ],
      },
    ];

    const found = configs.find((c) => templateId && templateId.includes(c.match));
    const overlayConfig = found?.overlayConfig || { x: 336, y: 90, width: 230, height: 230, clipAsCircle: true };
    const textConfigs = found?.textConfigs || [{ x: 30, y: 190, maxWidth: 310, ...baseTextConfig }];

    const textEntries = textConfigs.map((cfg) => ({
      value: formData.title || "",
      config: {
        ...cfg,
        ...baseTextConfig
      },
    }));

    if (variant === "agri-expert") {
      let agriExperNameConfig = {...textConfigs[0]};
      if(selectedThumbnail?.id === "t1"){
        agriExperNameConfig.x = 36;
        agriExperNameConfig.y = 50;
        agriExperNameConfig.maxWidth = 200;
      } else if(selectedThumbnail?.id === "t2"){
        agriExperNameConfig.x = 26;
        agriExperNameConfig.y = 30;
        agriExperNameConfig.maxWidth = 200;
      } else if(selectedThumbnail?.id === "t3"){
        agriExperNameConfig.x = 400;
        agriExperNameConfig.y = 375;
        agriExperNameConfig.maxWidth = 200;
      } else if(selectedThumbnail?.id === "t4"){
        agriExperNameConfig.x = 36;
        agriExperNameConfig.y = 375;
        agriExperNameConfig.maxWidth = 200;
      }
      textEntries.push({
        value: user.name,
        config: {
          ...agriExperNameConfig,
          ...baseTextConfig,
          color: "#006836"
        },
      });
    }

    return {
      overlayConfig,
      textEntries,
      canvasWidth: 600,
      canvasHeight: 400,
    };
  };

  const composeFinalImage = async (): Promise<string | null> => {
    let templateSrc = formData.templateImageUrl || selectedThumbnail?.localUrl || selectedThumbnail?.resolvedUrl;
    const firstImage = mediaList.find((m) => (m.mediaType || m.type) === UploaderMediaType.IMAGE && !m.isDeleted);
    let overlaySrc = firstImage?.file
      ? URL.createObjectURL(firstImage.file)
      : firstImage?.url || formData.thumbnailImageUrl;

    if (!templateSrc || !overlaySrc) {
      return null;
    }

    try {
      setIsComposing(true);

      const { overlayConfig, textEntries, canvasWidth, canvasHeight } = getTemplateComposeConfig(selectedThumbnail?.id, templateSrc);

      const { dataUrl } = await composeTemplateImage({
        canvasWidth,
        canvasHeight,
        templateSrc,
        overlayImageSrc: overlaySrc,
        text: textEntries,
        overlayConfig,
      });
      setComposedPreviewUrl(dataUrl);
      if (overlaySrc?.startsWith("blob:")) {
        URL.revokeObjectURL(overlaySrc);
      }
      if (templateSrc?.startsWith("blob:")) {
        URL.revokeObjectURL(templateSrc);
      }
      return dataUrl;
    } catch (err) {
      console.error(err);
      Error(language("msg.failed_to_generate_image") || "Failed to generate preview image");
      return null;
    } finally {
      setIsComposing(false);
    }
  };

  const goNext = async () => {
    if (isLoadingStory) return;
    if (step === 1) {
      if (!validateStepOne()) return;
      setStep(2);
    } else if (step === 2) {
      if (!formData.thumbnailImageUrl || !mediaList.find((m) => (m.mediaType || m.type) === "IMAGE" && !m.isDeleted)) {
        Error(language("msg.image_required") || "Please upload at least one image to continue");
        return;
      }
      const composed = await composeFinalImage();

      if (composed) {
        setStep(3);
      }
    }
  };

  const goBack = () => {
    if (step === 1) return;
    setStep((prev) => (prev === 2 ? 1 : 2));
  };

  const uploadMedia = async (file: File, mediaType: string = 'IMAGE'): Promise<any | null> => {
    try {
      const form = new FormData();
      form.set("file", file);
      form.set("assetId", "1");
      form.set("mediaType", mediaType);
      form.set("assetType", "USER_STORY");
      form.set("metadata", "");

      const res = await uploadMediaFile(form);
      const response = res?.data?.response;
      if (!response) {
        throw Error("Upload did not return url");
      }
      return response;
    } catch (err) {
      console.error(err);
      Error(language("msg.failed_to_upload_image") || "Failed to upload image");
      return null;
    }
  };

  const uploadFinalImage = async (): Promise<any | null> => {
    const finalDataUrl = composedPreviewUrl || (await composeFinalImage());
    if (!finalDataUrl) return null;
    const file = dataUrlToFile(finalDataUrl, `story-${Date.now()}.png`);
    return uploadMedia(file, "IMAGE");
  };

  const saveStory = async () => {
    setIsSaving(true);
    const finalThumbnail = await uploadFinalImage();

    const uploadedMediaList: any[] = [];
    for (const media of mediaList) {
      if (media.isDeleted) {
        deleteMediaFile(media.id)
        continue;
      }

      const mediaType = (media.mediaType || media.type || "IMAGE") as UploaderMediaType;

      if (media.url && !media.file && !media.url.startsWith("blob:")) {
        uploadedMediaList.push({
          id: media.id,
          url: media.url,
          mediaType,
        });
        continue;
      }

      let fileToUpload: File | null = null;
      if (media.file) {
        fileToUpload = media.file;
      } else if (media.url) {
        fileToUpload = await blobUrlToFile(media.url);
      }
      if (fileToUpload) {
        const uploaded = await uploadMedia(fileToUpload, mediaType);
        if (uploaded) {
          uploadedMediaList.push(uploaded);
        }
      } else if (mediaType === "YOUTUBE_VIDEO" && media.url) {
        uploadedMediaList.push({
          id: media.id,
          url: media.url,
          mediaType: "YOUTUBE_VIDEO",
        });
      }
    }

    if (!finalThumbnail) {
      setIsSaving(false);
      Error(language("msg.image_required") || "Preview image missing");
      return;
    }
    if (uploadedMediaList.length === 0) {
      setIsSaving(false);
      Error(language("msg.image_required") || "Please upload at least one media file");
      return;
    }

    const payload = {
      ...formData,
      templateImageUrl: formData.templateImageUrl,
      thumbnailImageUrl: finalThumbnail.url || finalThumbnail,
      userId: user?.phone_number,
      mediaAccessList: uploadedMediaList,
      isExpert: false,
      status: "PENDING_APPROVAL"
    };

    // align payload for agri expert (no rating, add experience)
    if (variant === "agri-expert") {
      payload.isExpert = true;
      payload.totalExperience = formData.totalExperience;
      delete payload.rating;
    }

    try {
      if (editStoryId) {
        payload.id = editStoryId;
        await updateStory(payload);
      } else {
        await createStory(payload);
      }
      Success(language("msg.save_success") || "Story saved successfully");
      navigate(`/postings/${variant}`)
    } catch (err) {
      console.error(err);
      Error(language("msg.save_failed") || "Failed to save story");
    } finally {
      setIsSaving(false);
    }
  };

  const renderStepOne = () => (
    <div className="">
      <h6 className="mb-3">{language("label.select_thumbnail") || "Select a Thumbnail Style"}</h6>
      <div className="d-flex gap-1 justify-content-between align-items-center">
        {thumbnailState.map((t, index) => (
          <div key={t.id || index} className="position-relative" onClick={() => selectThumbnailTemplate(index)}>
            <div
              className="position-absolute top-0 end-0 me-2 mt-1 rounded-circle kg-bg-dark text-center"
              style={{ width: "24px", height: "23px" }}
            >
              <i
                className={classNames("kg-white bi", {
                  "bi-circle": !t.isSelected,
                  "bi-check-circle-fill": t.isSelected,
                })}
              ></i>
            </div>
            <img src={t.resolvedUrl} alt={`Thumbnail ${index + 1}`} className="img-fluid" />
          </div>
        ))}
      </div>

      <div className="row mt-4">
        {includeRating && (
          <div className="col-xl-6 col-lg-6 col-md-12 mb-3">
            <label className="form-label fs-7">
              {language("label.rating") || "Overall Product Rating"}
              <small className="kg-danger">*</small>
            </label>
            <Ratings
              value={formData.rating || 0}
              readOnly={false}
              onChange={(value) => updateField("rating", value)}
              className="fs-2"
            />
            {renderError(errors, "rating", undefined, "Rating")}
          </div>
        )}
      </div>

      <div className="row">
        {fields.map((field) => (
          <div className={field.colClass || "col-xl-6 col-lg-6 col-md-12 mb-3"} key={field.name}>
            <label className="form-label fs-7">{field.label} <small className="kg-danger">*</small></label>
            {field.type === "textarea" ? (
              <textarea
                className="form-control rounded-3"
                rows={5}
                placeholder={field.placeholder}
                value={formData[field.name]}
                maxLength={field.maxLength}
                onChange={(e) => updateField(field.name, e.target.value)}
              />
            ) : (
              <input
                type="text"
                className="form-control rounded-3"
                placeholder={field.placeholder}
                value={formData[field.name]}
                maxLength={field.maxLength}
                onChange={(e) => updateField(field.name, e.target.value)}
              />
            )}
            {field.maxLength && (
              <div className="text-end small text-muted mt-1">
                {(formData[field.name] || "").length}/{field.maxLength}
              </div>
            )}
            {renderError(errors, field.name, undefined, field.label)}
          </div>
        ))}
      </div>
    </div>
  );

  const renderStepTwo = () => (
    <div className="">
      <h6 className="mb-3">{language("label.add_photo_video") || "Upload your media"}</h6>
      <MediaUploader
        initialMedia={mediaList.map((m) => ({
          id: m.id,
          url: m.url,
          file: m.file,
          type: ((m.type as UploaderMediaType) || (m.mediaType as UploaderMediaType) || "IMAGE") as UploaderMediaType,
        }))}
        allowVideo={true}
        allowYoutube={true}
        maxImages={4}
        uploadStrategy="local"
        assetType="USER_STORY"
        assetId={2}
        mediaType={UploaderMediaType.IMAGE}
        onChange={(items) => {
          const normalized: StoryMediaItem[] = items.map((i: UploaderMediaItem) => {
            const mType = (i.type as UploaderMediaType) || "IMAGE";
            const isNew = Boolean(i.file);
            const previewUrl = i.url || (i.file ? URL.createObjectURL(i.file) : "");
            return {
              id: i.id,
              url: previewUrl,
              file: i.file,
              mediaType: mType,
              type: mType,
              isNew: isNew,
              isDeleted: false,
            };
          });
          setMediaList(normalized);
          const image = normalized.find((i) => i.mediaType === "IMAGE" && !i.isDeleted);
          const previewUrl = image?.file ? URL.createObjectURL(image.file) : image?.url || "";
          if (previewUrl) {
            updateField("thumbnailImageUrl", previewUrl);
          }
        }}
      />
    </div>
  );

  const renderStepThree = () => (
    <div className="">
      <h6 className="mb-3">{language("label.story_preview") || "Preview"}</h6>
      <StoryPreviewCard
        title={formData.title}
        story={formData.story || formData.experience}
        previewImageUrl={composedPreviewUrl || ""}
        mediaList={mediaList}
        rating={formData.rating}
        showRating={includeRating}
      />
    </div>
  );

  return (
    <MainAdsLayout sideBanner={sideBanner}>
      <PageTitle headingPart1={headingPart1} headingPart2={headingPart2} />
      <BreadCrumb crumbs={breadcrumbs} />

      <div className="pe-lg-3 mt-2">
        <StepsIndicator steps={steps} activeStep={step} className="my-5" />
        {step === 1 && renderStepOne()}
        {step === 2 && renderStepTwo()}
        {step === 3 && renderStepThree()}


        <div className="d-flex justify-content-between align-items-center mt-3">
          <Button onClick={goBack} secondary outline rounded disabled={step === 1}>
            <i className="bi bi-chevron-left me-1" />
            {language("label.back") || "Back"}
          </Button>
          {step < 3 && (
            <Button primary solid rounded onClick={goNext} disabled={isComposing}>
              <span className="me-1">{language("label.next") || "Next"}</span>
              <i className="bi bi-chevron-right" />
            </Button>
          )}
          {isLastStep && (
            <Button primary solid rounded onClick={saveStory} disabled={isSaving}>
              {isSaving && <span className="spinner-border spinner-border-sm me-2" role="status"></span>}
              {language("label.submit") || "Submit"}
            </Button>
          )}
        </div>
      </div>
    </MainAdsLayout>
  );
}
